import { type FC } from "react";

import legalAuthorityImg from "@/shared/assets/illustrations/legal-authority-scale.svg";
import assetSearchCalcImg from "@/shared/assets/illustrations/asset-search-calculator.svg";
import someoneElseImg from "@/shared/assets/illustrations/someone-else-authority.svg";

import type { AuthorityPath } from "@/shared/utils/authorityPath";

import { HeroCard } from "./HeroCard";
import type { HomeState } from "../resolveHomeState";

interface AuthorityHeroCardConfig {
  bgColor: string;
  illustration: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaSubtext?: string;
}

function getNoAuthorityConfig(
  personName: string,
  authorityPath: AuthorityPath | null,
  hasCompletedAuthorityModal: boolean,
): AuthorityHeroCardConfig {
  if (authorityPath === "sea") {
    return {
      bgColor: "bg-blue-100",
      illustration: legalAuthorityImg,
      title: "Your path to legal authority: the small estate process",
      description: `${personName}'s estate may qualify for the small estate process, a shorter court path with less paperwork. Your roadmap lays out each step in order, so you always know what comes next.`,
      ctaLabel: "Start the process",
    };
  }
  if (authorityPath === "probate") {
    return {
      bgColor: "bg-blue-100",
      illustration: legalAuthorityImg,
      title: "Your path to legal authority: probate",
      description: `For ${personName}'s estate, legal authority comes through probate, the court process that confirms the will is real and names the executor. Your roadmap lays out each step in order, so you always know what comes next.`,
      ctaLabel: "Start probate",
    };
  }
  if (hasCompletedAuthorityModal) {
    return {
      bgColor: "bg-blue-100",
      illustration: legalAuthorityImg,
      title: "Your path to legal authority: administration",
      description: `For ${personName}'s estate, legal authority comes through administration. Since there isn't a will, the court names an administrator, usually the closest relative. Your roadmap lays out each step in order, so you always know what comes next.`,
      ctaLabel: "Start administration",
    };
  }
  return {
    bgColor: "bg-blue-100",
    illustration: legalAuthorityImg,
    title: `Get legal authority for ${personName}'s estate`,
    description: `Legal authority is the court paper that lets you act for the estate. Banks won't close accounts or release money until you have it. Answer a few questions to see which court path fits ${personName}'s estate.`,
    ctaLabel: "Find your path",
    ctaSubtext: "Free · About 5 minutes",
  };
}

function getConfig(
  state: HomeState,
  personName: string,
  isSearchRunning: boolean,
  authorityPath: AuthorityPath | null,
  hasCompletedAuthorityModal: boolean,
  nomineeName?: string,
): AuthorityHeroCardConfig {
  switch (state) {
    case "no-authority":
      return getNoAuthorityConfig(personName, authorityPath, hasCompletedAuthorityModal);
    case "has-authority":
      return isSearchRunning
        ? {
            bgColor: "bg-purple-100",
            illustration: assetSearchCalcImg,
            title: "Elayne Asset Search is running",
            description:
              "Findings are added to the Estate inventory as they come in, each with where it came from. The search covers 10 years of IRS records and 100+ other sources.",
            ctaLabel: "Review findings",
          }
        : {
            bgColor: "bg-purple-100",
            illustration: assetSearchCalcImg,
            title: "You're ready for Elayne Asset Search",
            description: `Find the retirement plans, bank accounts, life insurance, and property ${personName} left behind. With your legal authority, Elayne can request 10 years of ${personName}'s IRS records and check 100+ other sources.`,
            ctaLabel: "Get started",
          };
    case "settle-estate":
      return {
        bgColor: "bg-green-100",
        illustration: assetSearchCalcImg,
        title: "Your estate inventory is ready",
        description:
          "Elayne Asset Search checked 10 years of IRS records and 100+ other sources. Each finding is listed with where it came from. Next, use the roadmap to start closing accounts and passing on what's left.",
        ctaLabel: "Review findings",
      };
    case "someone-else-authority": {
      const name = nomineeName || "the executor";
      return {
        bgColor: "bg-purple-100",
        illustration: someoneElseImg,
        title: `The estate is waiting on ${name}, but you don't have to`,
        description: `${name}'s invite went out on your behalf, and we'll follow up if there's no response. In the meantime, everything you add builds the estate record ${name} will pick up: documents, contacts, and anything you know about accounts.`,
        ctaLabel: "Add documents",
      };
    }
    default:
      return {
        bgColor: "bg-blue-100",
        illustration: legalAuthorityImg,
        title: "Get authorized",
        description: "Pick up where you left off.",
        ctaLabel: "Continue",
      };
  }
}

interface AuthorityHeroCardProps {
  state: HomeState;
  personName: string;
  nomineeName?: string;
  authorityPath?: AuthorityPath | null;
  hasCompletedAuthorityModal?: boolean;
  isSearchRunning?: boolean;
  onCtaClick: () => void;
}

export const AuthorityHeroCard: FC<AuthorityHeroCardProps> = ({
  state,
  personName,
  nomineeName,
  authorityPath,
  hasCompletedAuthorityModal,
  isSearchRunning,
  onCtaClick,
}) => {
  const config = getConfig(
    state,
    personName,
    !!isSearchRunning,
    authorityPath ?? null,
    !!hasCompletedAuthorityModal,
    nomineeName,
  );

  return (
    <HeroCard
      bgColor={config.bgColor}
      illustration={config.illustration}
      title={config.title}
      description={config.description}
      ctaLabel={config.ctaLabel}
      ctaSubtext={config.ctaSubtext}
      onCtaClick={onCtaClick}
    />
  );
};
